// Shared ownership readout bar + legend — the stacked Owned/Influenced/Earned
// bar that appears both on the dashboard reputation card (RGS-4, link mode →
// each segment deep-links into Sources) and atop the Sources page (RGS-5,
// toggle mode → each segment filters the list in place, with the inactive
// orbits dimmed). One component so the two stay pixel-identical; the fiddly
// bits (sub-8% label hiding, wrap-safe legend) live here only.
//
// Styling: the `.rgs-*` classes in vb-reputation.css (scoped to .vb-root).

import React from "react";
import { Link } from "react-router-dom";
import type { Orbit, OrbitSummary } from "@/types/vb";

/** Display copy per orbit: technical label (breakdown/chips), friendly metaphor
 *  (legend), and the hover-tooltip definition. Order is the control gradient:
 *  owned (full control) → influenced (some sway) → earned (none). Tooltips are
 *  deliberately em-dash-free: they render verbatim via CSS `content: attr()`. */
export const ORBIT_META: Record<Orbit, { tech: string; friendly: string; tip: string }> = {
  owned: {
    tech: "Owned",
    friendly: "Your word",
    tip: "Pages you publish yourself, like your website and your Google and social profiles. You fully control what they say.",
  },
  influenced: {
    tech: "Influenced",
    friendly: "You can sway",
    tip: "Review sites and listings about you. You can shape these with effort, but you don't control them.",
  },
  earned: {
    tech: "Earned",
    friendly: "Strangers",
    tip: "Independent sites like press, registries and communities. Some are general references the AI looked up, not sites about you.",
  },
};

export const ORBIT_ORDER: Orbit[] = ["owned", "influenced", "earned"];

/** Plain-English caveat for the "earned" / "Strangers" bucket. The orbit
 *  classifier keys off the domain, so a source lands here whenever it's an
 *  independent site — including generic reference the model looked up to answer
 *  a question (a dictionary entry, say), which says nothing about the company.
 *  Rendered as always-visible copy under the bar, since the hover tip above
 *  isn't reachable on touch devices. */
export const EARNED_CAVEAT =
  'Some "Strangers" are general references the AI looked up, like a dictionary, not sites about you.';

/** The orbit holding the largest citation share — the one the lede speaks to.
 *  Ties resolve to the less-controlled orbit (earned > influenced > owned), so
 *  the readout never overstates how much of the picture the company controls. */
export function dominantOrbit(orbits: OrbitSummary[]): Orbit {
  let dominant: Orbit = ORBIT_ORDER[0];
  let max = -1;
  for (const orbit of ORBIT_ORDER) {
    const pct = orbits.find((o) => o.orbit === orbit)?.percentage ?? 0;
    if (pct >= max) {
      max = pct;
      dominant = orbit;
    }
  }
  return dominant;
}

const LEDE_FOR: Record<Orbit, (pct: number) => string> = {
  owned: (pct) =>
    `You're in control of how AI sees you. ${pct}% of citations come from sources you own.`,
  influenced: (pct) =>
    `You're influencing how AI sees you. ${pct}% of citations come from sources you can sway.`,
  // "Most" only when earned is an actual majority. As a plurality below 50% it
  // would contradict its own number ("Most ... 40%"), so say "the biggest share".
  earned: (pct) => {
    const lead = pct > 50 ? "Most of how AI sees you is earned" : "The biggest share of how AI sees you is earned";
    return `${lead}. ${pct}% of citations come from sources you don't control.`;
  },
};

/** Deterministic, statement-full readout lede: names the dominant orbit and its
 *  share. Falls back to a neutral line when nothing is orbit-classified yet
 *  (citations exist but every one is unclassified, so all shares are 0). */
export function reputationLede(orbits: OrbitSummary[]): string {
  const dominant = dominantOrbit(orbits);
  const pct = orbits.find((o) => o.orbit === dominant)?.percentage ?? 0;
  if (pct <= 0) return "Here's who controls the sources AI cited while scoring you.";
  return LEDE_FOR[dominant](pct);
}

/** Below this width a segment can't fit its % label, so we drop it (the legend
 *  beneath the bar carries the same info). */
const TINY_PCT = 8;

const ZERO = (orbit: Orbit): OrbitSummary => ({
  orbit,
  citations: 0,
  percentage: 0,
  sources_count: 0,
  top_domains: [],
});

interface OrbitBarProps {
  orbits: OrbitSummary[];
  /** Link mode (dashboard card): each segment is a Link to this href. */
  hrefFor?: (orbit: Orbit) => string;
  /** Toggle mode (Sources page): the active orbit filter ("all" = none). */
  activeOrbit?: Orbit | "all";
  /** Toggle mode: called with the clicked orbit (caller decides toggle-off). */
  onSelect?: (orbit: Orbit) => void;
}

export function OrbitBar({ orbits, hrefFor, activeOrbit, onSelect }: OrbitBarProps) {
  const byOrbit = (orbit: Orbit) => orbits.find((o) => o.orbit === orbit) ?? ZERO(orbit);
  const filtering = activeOrbit != null && activeOrbit !== "all";

  return (
    <div>
      <div className={`rgs-bar${filtering ? " rgs-bar--filtered" : ""}`}>
        {ORBIT_ORDER.map((orbit) => {
          const o = byOrbit(orbit);
          const meta = ORBIT_META[orbit];
          const active = activeOrbit === orbit;
          const cls =
            `rgs-seg rgs-seg--${orbit}` +
            (o.percentage < TINY_PCT ? " rgs-seg--tiny" : "") +
            (active ? " rgs-seg--active" : "");
          const verb = hrefFor ? "See" : "Filter to";
          const aria = `${meta.tech}, ${o.percentage}% of citations. ${verb} ${meta.tech.toLowerCase()} sources.`;

          if (hrefFor) {
            return (
              <Link
                key={orbit}
                className={cls}
                style={{ width: `${o.percentage}%` }}
                to={hrefFor(orbit)}
                data-tip={meta.tip}
                aria-label={aria}
              >
                {o.percentage}%
              </Link>
            );
          }
          return (
            <button
              key={orbit}
              type="button"
              className={cls}
              style={{ width: `${o.percentage}%` }}
              onClick={() => onSelect?.(orbit)}
              data-tip={meta.tip}
              aria-pressed={active}
              aria-label={aria}
            >
              {o.percentage}%
            </button>
          );
        })}
      </div>
      <div className="rgs-legend">
        {ORBIT_ORDER.map((orbit) => (
          <span className="rgs-legend-item" key={orbit}>
            <span className={`rgs-dot rgs-dot--${orbit}`} aria-hidden="true" />
            <span className="rgs-legend-label">{ORBIT_META[orbit].friendly}</span>
            <span className="rgs-legend-pct">{byOrbit(orbit).percentage}%</span>
          </span>
        ))}
      </div>
      {byOrbit("earned").percentage > 0 && <p className="rgs-caveat">{EARNED_CAVEAT}</p>}
    </div>
  );
}

export default OrbitBar;
